import httpClient from "../httpClient";

export interface Audience {
  id: string;
  name: string;
  userCount: number;
  checked: boolean;
  role?: number;
  department?: string;
}

export interface NotificationRecord {
  id: string;
  uuid?: string;
  title: string;
  subtitle: string;
  type: 'email' | 'in-app' | 'both';
  audiences: string[];
  status: 'sent' | 'scheduled' | 'draft' | 'sending' | 'failed';
  date: string;
  dateLabel: string;
  openRate?: number;
  clickRate?: number;
  message: string;
  targetAudiences: Array<{ name: string; color: string }>;
  sentDate: string;
}

export interface CampaignData {
  title: string;
  subject: string;
  message: string;
  targetAudience: {
    roles?: number[];
    departments?: string[];
    specificUsers?: string[];
  };
  deliveryMethod: 'email' | 'in-app' | 'both';
  scheduledFor?: string | null;
}

const ENDPOINTS = {
  CAMPAIGNS: "/admin/marketing/campaigns",
  AUDIENCES: "/admin/marketing/audiences",
  SEND_CAMPAIGN: (uuid: string) => `/admin/marketing/campaigns/${uuid}/send`,
  VALIDATE_EMAILS: "/admin/marketing/validate-emails",
};

export const getAudiences = async (): Promise<{
  success: boolean;
  data?: Audience[];
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<any>(ENDPOINTS.AUDIENCES);

    if (response.success && response.data) {
      return {
        success: true,
        data: response.data,
        error: null
      };
    }

    return {
      success: false,
      error: { message: response.error?.message || 'Failed to fetch audiences' }
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch audiences' }
    };
  }
};

export const getNotificationHistory = async (): Promise<{
  success: boolean;
  data?: NotificationRecord[];
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<any>(ENDPOINTS.CAMPAIGNS);

    if (response.success && response.data) {
      const records: NotificationRecord[] = response.data.map((campaign: any) => {
        // Parse target_audience if it's a string
        let targetAudience = campaign.targetAudience || campaign.target_audience;
        if (typeof targetAudience === 'string') {
          try {
            targetAudience = JSON.parse(targetAudience);
          } catch (e) {
            targetAudience = { roles: [], departments: [], specificUsers: [] };
          }
        }

        // Map role IDs to names
        const getRoleName = (roleId: number) => {
          switch (roleId) {
            case 1: return 'Students';
            case 2: return 'Instructors';
            case 3: return 'Admins';
            case 4: return 'Super Admins';
            default: return 'Unknown';
          }
        };

        const roles = targetAudience?.roles || [];
        const specificUsers = targetAudience?.specificUsers || [];

        // Build audience list
        const audiences = [
          ...roles.map((r: number) => getRoleName(r)),
          ...(specificUsers.length > 0 ? [`${specificUsers.length} specific user(s)`] : [])
        ];

        // Format date
        const formatDate = (dateStr: string) => {
          if (!dateStr) return '-';
          const date = new Date(dateStr);
          return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          });
        };

        return {
          id: campaign.id?.toString() || campaign.uuid,
          uuid: campaign.uuid,
          title: campaign.title,
          subtitle: campaign.message?.substring(0, 30) + '...',
          type: campaign.deliveryMethod || campaign.delivery_method || 'both',
          audiences: audiences,
          status: campaign.status || 'draft',
          date: formatDate(campaign.sentAt || campaign.sent_at || campaign.scheduledFor || campaign.scheduled_for || campaign.createdAt || campaign.created_at),
          dateLabel: campaign.status === 'sent' ? 'Sent' :
                     campaign.status === 'scheduled' ? 'Scheduled' :
                     campaign.status === 'sending' ? 'Sending' : 'Draft',
          openRate: campaign.totalOpened || campaign.total_opened && campaign.totalSent || campaign.total_sent ?
                    Math.round(((campaign.totalOpened || campaign.total_opened) / (campaign.totalSent || campaign.total_sent)) * 100) : undefined,
          clickRate: campaign.totalClicked || campaign.total_clicked && campaign.totalSent || campaign.total_sent ?
                     Math.round(((campaign.totalClicked || campaign.total_clicked) / (campaign.totalSent || campaign.total_sent)) * 100) : undefined,
          message: campaign.message,
          targetAudiences: roles.map((r: number) => ({
            name: getRoleName(r),
            color: '#f59e0b'
          })),
          sentDate: formatDate(campaign.sentAt || campaign.sent_at || campaign.createdAt || campaign.created_at)
        };
      });

      return { success: true, data: records, error: null };
    }

    return { success: false, data: [], error: { message: response.error?.message || 'Failed to fetch campaigns' } };
  } catch (error: any) {
    return { success: false, error: { message: error?.message ?? 'Failed to fetch notification history' } };
  }
};

export const createCampaign = async (campaignData: CampaignData): Promise<{
  success: boolean;
  data?: any;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.post<any>(ENDPOINTS.CAMPAIGNS, campaignData);
    return response;
  } catch (error: any) {
    return { success: false, error: { message: error?.message ?? 'Failed to create campaign' } };
  }
};

export const sendCampaign = async (uuid: string): Promise<{
  success: boolean;
  data?: any;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.post<any>(ENDPOINTS.SEND_CAMPAIGN(uuid), {});
    return response;
  } catch (error: any) {
    return { success: false, error: { message: error?.message ?? 'Failed to send campaign' } };
  }
};

export const updateCampaign = async (uuid: string, data: Partial<CampaignData>): Promise<{
  success: boolean;
  data?: any;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.put<any>(`${ENDPOINTS.CAMPAIGNS}/${uuid}`, data);
    return response;
  } catch (error: any) {
    return { success: false, error: { message: error?.message ?? 'Failed to update campaign' } };
  }
};

export const deleteCampaign = async (uuid: string): Promise<{
  success: boolean;
  data?: any;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.delete<any>(`${ENDPOINTS.CAMPAIGNS}/${uuid}`);
    return response;
  } catch (error: any) {
    return { success: false, error: { message: error?.message ?? 'Failed to delete campaign' } };
  }
};

export const validateEmails = async (emails: string[]): Promise<{
  success: boolean;
  data?: {
    validEmails: string[];
    invalidEmails: string[];
  };
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.post<any>(ENDPOINTS.VALIDATE_EMAILS, { emails });
    return response;
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to validate emails' }
    };
  }
};
